import React, { useEffect, useState } from "react";
import { LineChart, Line, ReferenceLine, ResponsiveContainer, YAxis, Area, AreaChart, Tooltip } from "recharts";
import { Activity, Loader2, TrendingUp, TrendingDown, Plus } from "lucide-react";
import { getDetail } from "@/lib/api";
import { sigMeta, fmt, cur, pct, UP, DOWN } from "@/lib/format";

const Gauge = ({ label, value, invert }) => {
  const v = value ?? 0;
  const hot = invert ? v <= 8 : v >= 92;
  const color = invert ? UP : DOWN;
  return (
    <div className="flex-1">
      <div className="flex items-center justify-between mb-1">
        <span className="font-semibold text-[11px] tracking-wider text-slate-500">{label}</span>
        <span className="font-mono text-[12px] font-bold tabular-nums" style={{ color: hot ? color : "#0F172A" }}>{fmt(v, 0)}</span>
      </div>
      <div className="h-1.5 rounded-full bg-slate-200 overflow-hidden">
        <div className="h-full rounded-full transition-all duration-500" style={{ width: `${Math.max(2, Math.min(100, v))}%`, background: hot ? color : "#94A3B8" }} />
      </div>
    </div>
  );
};

const Panel = ({ title, children, action }) => (
  <div className="border border-[#E2E8F0] bg-[#F8FAFC] rounded-[4px] p-3">
    <div className="flex items-center justify-between mb-2">
      <span className="font-semibold text-[11px] tracking-[0.14em] uppercase text-slate-500">{title}</span>
      {action}
    </div>
    {children}
  </div>
);

const MetricStat = ({ label, value, pass, suffix = "", hint }) => (
  <div className="rounded-[3px] border p-2" style={{ background: pass === true ? "#ECFDF5" : pass === false ? "#FFFFFF" : "#FFFFFF", borderColor: pass === true ? "#A7F3D0" : "#E2E8F0" }}>
    <div className="text-[10px] uppercase tracking-wide text-slate-500">{label}</div>
    <div className="font-mono text-[13px] font-bold tabular-nums" style={{ color: pass === true ? UP : pass === false ? "#0F172A" : "#64748B" }}>
      {value === null || value === undefined ? "—" : `${value}${suffix}`}
    </div>
    {hint && <div className="text-[10px] text-slate-400 mt-0.5">{hint}</div>}
  </div>
);

const tooltipStyle = { background: "#FFFFFF", border: "1px solid #E2E8F0", borderRadius: 4, fontSize: 11, fontFamily: "JetBrains Mono, monospace", boxShadow: "0 2px 8px rgba(15,23,42,0.08)" };

const mergeByT = (defs) => {
  const map = new Map();
  Object.entries(defs).forEach(([key, arr]) => {
    (arr || []).forEach((pt) => {
      if (!map.has(pt.t)) map.set(pt.t, { t: pt.t });
      map.get(pt.t)[key] = pt.v;
    });
  });
  return Array.from(map.values());
};

export default function DeepDive({ ticker, onCompare }) {
  const [d, setD] = useState(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!ticker) { setD(null); return; }
    setLoading(true);
    getDetail(ticker).then(setD).catch(() => setD(null)).finally(() => setLoading(false));
  }, [ticker]);

  if (!ticker) {
    return (
      <div className="bg-white border border-[#E2E8F0] rounded-[4px] shadow-xs h-full flex flex-col items-center justify-center p-6 text-center" data-testid="deepdive-empty">
        <Activity size={26} className="text-slate-300 mb-3" />
        <div className="font-semibold text-[11px] text-slate-500 uppercase tracking-wider">Select a symbol</div>
        <div className="text-[11px] text-slate-400 mt-1">Technical &amp; fundamental inspector</div>
      </div>
    );
  }

  if (loading || !d) {
    return (
      <div className="bg-white border border-[#E2E8F0] rounded-[4px] shadow-xs h-full flex items-center justify-center" data-testid="deepdive-loading">
        <Loader2 size={18} className="text-slate-400 animate-spin" />
      </div>
    );
  }

  const m = sigMeta(d.signal);
  const up = (d.change_pct ?? 0) >= 0;
  const qc = d.quality_checks || {};
  const rsiData = (d.series?.rsi || []).map((x) => ({ t: x.t, v: x.v }));
  const macdData = mergeByT({ macd: d.series?.macd, sig: d.series?.macd_signal });
  const bbData = mergeByT({ close: d.series?.close, upper: d.series?.bb_upper, lower: d.series?.bb_lower });
  const adxData = mergeByT({ adx: d.series?.adx, dip: d.series?.di_pos, din: d.series?.di_neg });
  const dev = d.pe_dev_pct ?? 0;
  const devClamp = Math.max(-100, Math.min(100, dev));
  const bbPct = d.bb_percent_b === null || d.bb_percent_b === undefined ? null : Math.round(d.bb_percent_b * 100);

  return (
    <div className="bg-white border border-[#E2E8F0] rounded-[4px] shadow-xs h-full flex flex-col min-h-0" data-testid="deepdive-panel">
      <div className="p-3 border-b border-[#E2E8F0]">
        <div className="flex items-start justify-between">
          <div className="min-w-0">
            <div className="flex items-center gap-1.5">
              <span className="font-display text-lg font-extrabold text-slate-900 leading-none">{d.ticker.replace(".NS", "").replace("^", "")}</span>
              <span className="font-mono text-[11px] px-1 py-0.5 rounded-[3px] bg-[#F1F5F9] text-slate-500 border border-[#E2E8F0]">{d.asset_type && d.asset_type !== "STOCK" ? d.asset_type : (d.market === "IN" ? "NSE" : "US")}</span>
            </div>
            <div className="text-[12px] text-slate-500 mt-1 truncate max-w-[180px]">{d.name}</div>
          </div>
          <span className="font-semibold text-[11px] tracking-wide px-1.5 py-1 rounded-[3px] border inline-flex items-center gap-1" style={{ color: m.color, background: m.bg, borderColor: m.border }}>
            <span className="text-[7px]">{m.icon}</span>{m.label}
          </span>
        </div>
        <div className="flex items-baseline gap-2 mt-2">
          <span className="font-mono text-2xl font-black text-slate-900 tabular-nums">{cur(d.market)}{fmt(d.price)}</span>
          <span className="font-mono text-[12px] font-semibold tabular-nums px-1.5 py-0.5 rounded-[3px]"
            style={{ color: up ? "#065F46" : "#991B1B", background: up ? "#ECFDF5" : "#FEF2F2" }}>{pct(d.change_pct)}</span>
        </div>
        {onCompare && (
          <button onClick={() => onCompare(d.ticker)} data-testid="add-compare-btn"
            className="mt-2 inline-flex items-center gap-1.5 text-[11px] font-semibold px-2.5 py-1.5 rounded-[4px] border border-[#0070BA]/40 bg-[#0070BA]/8 text-[#0070BA] hover:bg-[#0070BA]/15 transition-colors">
            <Plus size={12} /> Add to Compare
          </button>
        )}
      </div>

      <div className="flex-1 overflow-y-auto min-h-0 p-3 space-y-3">
        <div className="text-[11px] font-bold uppercase tracking-[0.14em] text-slate-400">Technicals · Weekly</div>

        <Panel title="Stoch RSI Extreme (K / D)">
          <div className="flex gap-3">
            <Gauge label="K" value={d.stoch_k} invert={d.signal.includes("BUY")} />
            <Gauge label="D" value={d.stoch_d} invert={d.signal.includes("BUY")} />
          </div>
        </Panel>

        <Panel title="Weekly RSI · 80/20 Bands" action={<span className="font-mono text-[11px] font-bold tabular-nums" style={{ color: d.rsi > 80 ? DOWN : d.rsi < 28 ? UP : "#0F172A" }}>{fmt(d.rsi, 1)}</span>}>
          <div className="h-20">
            <ResponsiveContainer width="100%" height="100%">
              <LineChart data={rsiData} margin={{ top: 4, right: 2, left: 2, bottom: 0 }}>
                <YAxis domain={[0, 100]} hide />
                <ReferenceLine y={80} stroke={DOWN} strokeDasharray="3 3" strokeOpacity={0.6} />
                <ReferenceLine y={20} stroke={UP} strokeDasharray="3 3" strokeOpacity={0.6} />
                <Line type="monotone" dataKey="v" stroke="#0284C7" strokeWidth={1.6} dot={false} isAnimationActive={false} />
                <Tooltip contentStyle={tooltipStyle} labelStyle={{ color: "#64748B" }} />
              </LineChart>
            </ResponsiveContainer>
          </div>
        </Panel>

        <Panel title="MACD Crossover" action={<span className="font-mono text-[12px] font-semibold" style={{ color: d.macd_bull_cross ? UP : "#64748B" }}>{d.macd_bull_cross ? "BULLISH ↑" : "—"}</span>}>
          <div className="h-20">
            <ResponsiveContainer width="100%" height="100%">
              <AreaChart data={macdData} margin={{ top: 4, right: 2, left: 2, bottom: 0 }}>
                <ReferenceLine y={0} stroke="#CBD5E1" />
                <Area type="monotone" dataKey="macd" stroke="#0284C7" strokeWidth={1.5} fill="rgba(2,132,199,0.10)" dot={false} isAnimationActive={false} />
                <Line type="monotone" dataKey="sig" stroke="#D97706" strokeWidth={1.3} dot={false} isAnimationActive={false} />
                <Tooltip contentStyle={tooltipStyle} labelStyle={{ color: "#64748B" }} />
              </AreaChart>
            </ResponsiveContainer>
          </div>
        </Panel>

        <Panel title="Bollinger Bands (20, 2σ)" action={<span className="font-mono text-[12px] font-bold tabular-nums" style={{ color: bbPct >= 90 ? DOWN : bbPct <= 10 ? UP : "#0F172A" }}>%B {bbPct === null ? "—" : bbPct}</span>}>
          <div className="h-20">
            <ResponsiveContainer width="100%" height="100%">
              <LineChart data={bbData} margin={{ top: 4, right: 2, left: 2, bottom: 0 }}>
                <Line type="monotone" dataKey="upper" stroke={DOWN} strokeWidth={1} strokeDasharray="3 3" dot={false} isAnimationActive={false} />
                <Line type="monotone" dataKey="lower" stroke={UP} strokeWidth={1} strokeDasharray="3 3" dot={false} isAnimationActive={false} />
                <Line type="monotone" dataKey="close" stroke="#0F172A" strokeWidth={1.5} dot={false} isAnimationActive={false} />
                <Tooltip contentStyle={tooltipStyle} labelStyle={{ color: "#64748B" }} />
              </LineChart>
            </ResponsiveContainer>
          </div>
          <div className="text-[11px] text-slate-400 mt-1">%B ≥ 90 = riding upper band (overbought) · ≤ 10 = lower band (oversold)</div>
        </Panel>

        <Panel title="ADX / DMI · Trend Strength" action={
          <span className="font-semibold text-[11px] px-1.5 py-0.5 rounded-[3px] border inline-flex items-center gap-1"
            style={{ color: d.adx_direction === "BULL" ? "#065F46" : "#991B1B", background: d.adx_direction === "BULL" ? "#ECFDF5" : "#FEF2F2", borderColor: d.adx_direction === "BULL" ? "#A7F3D0" : "#FECACA" }}>
            {d.adx_direction === "BULL" ? <TrendingUp size={10} /> : <TrendingDown size={10} />}
            ADX {fmt(d.adx, 0)} {(d.adx ?? 0) >= 25 ? "STRONG" : "WEAK"}
          </span>}>
          <div className="h-20">
            <ResponsiveContainer width="100%" height="100%">
              <LineChart data={adxData} margin={{ top: 4, right: 2, left: 2, bottom: 0 }}>
                <YAxis domain={[0, 60]} hide />
                <ReferenceLine y={25} stroke="#94A3B8" strokeDasharray="3 3" strokeOpacity={0.7} />
                <Line type="monotone" dataKey="dip" stroke={UP} strokeWidth={1.2} dot={false} isAnimationActive={false} name="+DI" />
                <Line type="monotone" dataKey="din" stroke={DOWN} strokeWidth={1.2} dot={false} isAnimationActive={false} name="-DI" />
                <Line type="monotone" dataKey="adx" stroke="#7C3AED" strokeWidth={1.8} dot={false} isAnimationActive={false} name="ADX" />
                <Tooltip contentStyle={tooltipStyle} labelStyle={{ color: "#64748B" }} />
              </LineChart>
            </ResponsiveContainer>
          </div>
          <div className="flex items-center justify-between mt-1 text-[11px] font-mono">
            <span style={{ color: UP }}>+DI {fmt(d.di_pos, 1)}</span>
            <span className="text-[#7C3AED]">ADX &gt; 25 = strong trend</span>
            <span style={{ color: DOWN }}>-DI {fmt(d.di_neg, 1)}</span>
          </div>
        </Panel>

        <Panel title="On-Balance Volume · Smart Money" action={
          <span className="font-semibold text-[11px] px-1.5 py-0.5 rounded-[3px] inline-flex items-center gap-1"
            style={{ color: d.obv_trend === "UP" ? "#065F46" : d.obv_trend === "DOWN" ? "#991B1B" : "#64748B", background: d.obv_trend === "UP" ? "#ECFDF5" : d.obv_trend === "DOWN" ? "#FEF2F2" : "#F1F5F9" }}>
            {d.obv_trend === "UP" ? <TrendingUp size={10} /> : d.obv_trend === "DOWN" ? <TrendingDown size={10} /> : null}
            {d.obv_trend === "UP" ? "ACCUMULATION" : d.obv_trend === "DOWN" ? "DISTRIBUTION" : "—"}
          </span>}>
          <div className="text-[12px] text-slate-500">
            {d.obv_trend === "UP" ? "Volume flow rising above its 10-week average — buyers in control." : d.obv_trend === "DOWN" ? "Volume flow falling below its 10-week average — sellers in control." : "Insufficient volume data."}
          </div>
        </Panel>

        <div className="text-[11px] font-bold uppercase tracking-[0.14em] text-slate-400 pt-1">Fundamentals · Valuation &amp; Quality</div>

        <Panel title="5-Year P/E Valuation Band">
          <div className="flex justify-between text-[12px] mb-2">
            <span className="text-slate-500">Current <span className="font-mono text-slate-900 font-bold">{fmt(d.current_pe, 1)}</span></span>
            <span className="text-slate-500">5Y Avg <span className="font-mono text-slate-900 font-bold">{fmt(d.avg_5y_pe, 1)}</span></span>
          </div>
          <div className="relative h-2 rounded-full bg-slate-200">
            <div className="absolute top-1/2 -translate-y-1/2 left-1/2 w-px h-3 bg-slate-400" />
            <div className="absolute top-1/2 -translate-y-1/2 h-2 rounded-full"
              style={{ background: dev >= 0 ? DOWN : UP, left: dev >= 0 ? "50%" : `${50 + devClamp / 2}%`, width: `${Math.abs(devClamp) / 2}%` }} />
          </div>
          <div className="flex justify-between text-[11px] text-slate-400 mt-1 font-mono">
            <span>-50%</span>
            <span className="font-bold" style={{ color: dev >= 50 ? DOWN : dev <= -50 ? UP : "#64748B" }}>{pct(dev)} vs avg</span>
            <span>+50%</span>
          </div>
        </Panel>

        <Panel title="Top-Investor Screens" action={<span className="font-mono text-[12px] font-bold" style={{ color: d.high_quality ? UP : "#64748B" }}>{d.quality_score ?? 0}/5</span>}>
          <div className="grid grid-cols-3 gap-1.5">
            <MetricStat label="ROE" value={fmt(d.roe, 1)} suffix="%" pass={qc.roe} hint="≥15%" />
            <MetricStat label="PEG" value={fmt(d.peg, 2)} pass={qc.peg} hint="≤1.5" />
            <MetricStat label="P/B" value={fmt(d.pb, 2)} pass={qc.pb} hint="≤3" />
            <MetricStat label="EV/EBITDA" value={fmt(d.ev_ebitda, 1)} pass={qc.ev_ebitda} hint="≤15" />
            <MetricStat label="Net Margin" value={fmt(d.profit_margin, 1)} suffix="%" pass={qc.profit_margin} hint="≥10%" />
            <MetricStat label="Quality" value={d.high_quality ? "HIGH" : "AVG"} pass={d.high_quality ? true : undefined} />
          </div>
        </Panel>

        <Panel title="Resistance Zones · Pivot Highs" action={d.near_resistance ? <span className="font-semibold text-[11px] px-1.5 py-0.5 rounded-[3px]" style={{ color: "#991B1B", background: "#FEF2F2", border: "1px solid #EF4444" }}>AT RESISTANCE</span> : <span className="font-mono text-[11px] text-slate-400">CLEAR</span>}>
          {(d.resistance_zones && d.resistance_zones.length > 0) ? (
            <div className="space-y-1.5">
              {d.resistance_zones.map((z, i) => {
                const touching = z.distance_pct >= -2 && z.distance_pct <= 4;
                return (
                  <div key={`${z.level}-${i}`} className="flex items-center gap-2">
                    <span className="font-mono text-[11px] tabular-nums w-14 text-right font-medium" style={{ color: touching && z.strength >= 2 ? DOWN : "#0F172A" }}>{cur(d.market)}{fmt(z.level)}</span>
                    <div className="flex-1 h-1.5 rounded-full bg-slate-200 overflow-hidden">
                      <div className="h-full rounded-full" style={{ width: `${Math.min(100, z.strength * 22)}%`, background: touching && z.strength >= 2 ? DOWN : "#94A3B8" }} />
                    </div>
                    <span className="font-mono text-[11px] text-slate-400 w-8">×{z.strength}</span>
                    <span className="font-mono text-[11px] tabular-nums w-12 text-right" style={{ color: touching ? "#D97706" : "#64748B" }}>+{fmt(z.distance_pct, 1)}%</span>
                  </div>
                );
              })}
              <div className="text-[11px] text-slate-400 pt-1">Strength = pivot-high touches · overhead levels vs current price</div>
            </div>
          ) : (
            <div className="text-[12px] text-slate-400">No overhead resistance detected — price in blue-sky / needs rescan.</div>
          )}
        </Panel>

        <Panel title="Balance Sheet Health">
          <div className="grid grid-cols-2 gap-2">
            <div>
              <div className="text-[11px] text-slate-500 uppercase tracking-wide">Debt / Equity</div>
              <div className="font-mono text-[13px] font-bold tabular-nums" style={{ color: (d.debt_to_equity ?? 999) < 50 ? UP : "#0F172A" }}>{fmt(d.debt_to_equity, 1)}</div>
            </div>
            <div>
              <div className="text-[11px] text-slate-500 uppercase tracking-wide">Current Ratio</div>
              <div className="font-mono text-[13px] font-bold tabular-nums" style={{ color: (d.current_ratio ?? 0) > 1.5 ? UP : "#0F172A" }}>{fmt(d.current_ratio, 2)}</div>
            </div>
          </div>
          <div className="mt-2 text-[12px] font-medium" style={{ color: d.strong_balance_sheet ? UP : "#D97706" }}>
            {d.strong_balance_sheet ? "✓ Strong — passes health filters" : "△ Does not pass strict health filters"}
          </div>
        </Panel>

        {d.reasons?.length > 0 && (
          <Panel title="Strategy Rationale">
            <ul className="space-y-1">
              {d.reasons.map((r, i) => (
                <li key={`${r}-${i}`} className="text-[12px] text-slate-600 flex gap-1.5"><span style={{ color: m.color }}>›</span>{r}</li>
              ))}
            </ul>
          </Panel>
        )}
      </div>
    </div>
  );
}
